import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowUp, Loader2 } from "lucide-react";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatRelativeTime } from "@/lib/relative-time";
import { tools } from "@/lib/tools";

const promptInput = z.strictObject({
  prompt: z.string().min(1, "Bitte geben Sie eine Anfrage ein").max(10000),
});

export const Route = createFileRoute("/_authed/agents/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(
      context.tools.queryOptions("GET /api/agents", { limit: 20 }),
    ),
  component: Agents,
});

// One per agent capability (fee lookup, search, combination rules, treatment
// plans), so the start screen doubles as a map of what Tycho can do. Keep them
// in step with the tools listed in the agent's system prompt.
const examples = [
  "Was kostet GOZ 2197 zum 2,3-fachen Satz?",
  "Welche GOZ-Positionen gibt es für Wurzelkanalbehandlungen?",
  "Darf ich GOZ 0010 und GOZ 0030 in derselben Sitzung berechnen?",
  "Erstelle einen Behandlungsplan für eine dreiflächige Füllung an Zahn 36.",
];

// The sidebar already lists every conversation; here only a jump-back-in.
const RECENT_LIMIT = 5;

function Agents() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const request = useRef({ prompt: "", id: crypto.randomUUID() });
  const conversations = useQuery(tools.queryOptions("GET /api/conversations", {}));
  const runs = useQuery(tools.queryOptions("GET /api/agents", { limit: 20 }));

  const form = useForm<z.infer<typeof promptInput>>({
    resolver: zodResolver(promptInput),
    defaultValues: { prompt: "" },
  });

  const startRun = useMutation(
    tools.mutationOptions("POST /api/conversations", {
      onSuccess: async (conversation) => {
        form.reset();
        request.current = { prompt: "", id: crypto.randomUUID() };
        await queryClient.invalidateQueries({ queryKey: tools.queryKey("GET /api/agents") });
        // Straight to the detail view — the stream is already being written.
        await queryClient.invalidateQueries({ queryKey: tools.queryKey("GET /api/conversations") });
        await navigate({
          to: "/conversations/$conversationId",
          params: { conversationId: conversation.id },
        });
      },
    }),
  );

  // An example fills the box rather than sending: most need adapting (tooth,
  // factor, position), and a click should not silently start a paid model run.
  const fillExample = (text: string) => {
    form.setValue("prompt", text, { shouldValidate: false });
    form.clearErrors("prompt");
    const box = document.getElementById("prompt") as HTMLTextAreaElement | null;
    box?.focus();
    box?.setSelectionRange(text.length, text.length);
  };

  const now = new Date();
  const recent = conversations.data?.data.slice(0, RECENT_LIMIT) ?? [];

  return (
    <div className="mx-auto w-full max-w-2xl space-y-12 p-4 md:p-8">
      <div className="space-y-6 pt-[8vh]">
        <div className="space-y-2 text-center">
          <h1 className="font-serif text-3xl tracking-tight md:text-4xl">Wie kann ich helfen?</h1>
          <p className="text-muted-foreground text-sm">
            Fragen Sie nach GOZ-Positionen, Gebühren und Abrechnungsregeln – oder lassen Sie einen
            Behandlungsplan entwerfen.
          </p>
        </div>

        <form
          onSubmit={form.handleSubmit((values) => {
            if (startRun.isPending) return;
            if (request.current.prompt !== values.prompt)
              request.current = { prompt: values.prompt, id: crypto.randomUUID() };
            startRun.mutate({ ...values, idempotencyKey: request.current.id });
          })}
          noValidate
        >
          <div className="flex items-end gap-2 rounded-xl border bg-background p-2 transition-colors focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25">
            <label htmlFor="prompt" className="sr-only">
              Ihre Anfrage
            </label>
            <Textarea
              id="prompt"
              submitOnEnter
              rows={2}
              className="min-h-16 border-0 px-2 py-2 focus-visible:ring-0"
              placeholder="Ihre Frage an Tycho…"
              aria-invalid={Boolean(form.formState.errors.prompt)}
              aria-describedby={form.formState.errors.prompt ? "prompt-error" : undefined}
              {...form.register("prompt")}
            />
            <Button
              type="submit"
              size="icon"
              aria-label="Unterhaltung starten"
              className="size-10 shrink-0 rounded-lg"
              disabled={startRun.isPending}
            >
              {startRun.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ArrowUp className="size-4" />
              )}
            </Button>
          </div>
          {form.formState.errors.prompt ? (
            <p id="prompt-error" role="alert" className="mt-2 text-destructive text-sm">
              {form.formState.errors.prompt.message}
            </p>
          ) : null}
          {startRun.error ? (
            <p role="alert" className="mt-2 text-destructive text-sm">
              {startRun.error.message}
            </p>
          ) : null}
        </form>

        <ul className="grid gap-2 sm:grid-cols-2" aria-label="Beispielfragen">
          {examples.map((example) => (
            <li key={example}>
              <button
                type="button"
                onClick={() => fillExample(example)}
                className="h-full w-full rounded-lg border px-4 py-3 text-left text-muted-foreground text-sm transition-colors hover:border-foreground/25 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                {example}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {recent.length ? (
        <section className="space-y-2">
          <h2 className="font-medium text-[11px] text-muted-foreground uppercase tracking-wider">
            Letzte Unterhaltungen
          </h2>
          <ul className="divide-y border-t">
            {recent.map((conversation) => (
              <li key={conversation.id}>
                <Link
                  className="-mx-2 flex items-baseline gap-4 rounded-md px-2 py-3 text-sm hover:bg-muted"
                  to="/conversations/$conversationId"
                  params={{ conversationId: conversation.id }}
                >
                  <span className="min-w-0 flex-1 truncate">{conversation.title}</span>
                  <span className="shrink-0 text-muted-foreground text-xs">
                    {formatRelativeTime(new Date(conversation.updatedAt), now)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {runs.data?.data.length ? (
        <section className="space-y-3">
          <h2 className="font-medium text-[11px] text-muted-foreground uppercase tracking-wider">
            Frühere Ausführungen
          </h2>
          <ul className="divide-y border-t">
            {runs.data.data.map((run) => (
              <li key={run.id}>
                <Link
                  to="/agents/$runId"
                  params={{ runId: run.id }}
                  className="-mx-2 flex items-center gap-3 rounded-md px-2 py-3 hover:bg-muted"
                >
                  <StatusBadge status={run.status} />
                  <span className="truncate text-sm">{run.prompt}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
